import { Controller } from "@hotwired/stimulus"

// Channel-aware reply composer for the conversation screen. Send posts to the
// existing send_response endpoint (which dispatches through Instantly for email /
// HeyReach for LinkedIn and marks the lead handled), then STAYS ON THIS
// CONVERSATION and re-renders the message thread.
//
// It used to Turbo.visit the next waiting lead the instant a reply went out, which
// is what made a second consecutive message impossible: the send navigated you off
// the person before you could write the follow-up, and the lead had by then left
// the Respond queue, so there was no route back to them (KI-194).
//
// The re-render is deliberately narrow. See refreshThread: it replaces the contents
// of the thread container and nothing else, because the queue top bar holds a
// Snooze menu whose next_id was baked at first render and cannot be recomputed
// correctly once the reply has been sent. (The removal control beside it carries no
// next_id — it returns to the inbox — so it has no target to protect.)
export default class extends Controller {
  static targets = ["input", "send", "suggest", "hint", "error", "liToggle", "emToggle", "thread"]
  static values = {
    sendUrl: String,
    suggestUrl: String,
    threadUrl: String,
    channel: String
  }

  connect() {
    this.selected = this.channelValue || "linkedin"
  }

  pickLinkedin() {
    this.setChannel("linkedin")
  }

  pickEmail() {
    this.setChannel("email")
  }

  setChannel(channel) {
    this.selected = channel
    const on = "background:#4A32E5; color:#fff;"
    const off = "background:transparent; color:#8488A4;"
    const base =
      "padding:5px 11px; border:none; border-radius:7px; font-size:11.5px; font-weight:600; cursor:pointer; font-family:inherit; "
    if (this.hasLiToggleTarget) {
      this.liToggleTarget.style.cssText = base + (channel === "linkedin" ? on : off)
    }
    if (this.hasEmToggleTarget) {
      this.emToggleTarget.style.cssText = base + (channel === "email" ? on : off)
    }
    if (this.hasHintTarget) {
      this.hintTarget.textContent =
        channel === "email" ? "Goes out as an email" : "Goes out as a LinkedIn message"
    }
  }

  // On-demand AI draft: ask the suggest_reply endpoint for a suggestion on the
  // currently selected channel and drop it into the composer for the user to
  // edit before sending. Never fires on its own — only when the user clicks.
  async suggest() {
    if (!this.hasSuggestUrlValue || !this.suggestUrlValue) return
    this.clearError()

    const button = this.hasSuggestTarget ? this.suggestTarget : null
    const original = button ? button.textContent : null
    if (button) {
      button.disabled = true
      button.textContent = "Drafting…"
    }

    try {
      const response = await fetch(this.suggestUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ platform_type: this.selected })
      })

      const data = await response.json()

      if (response.ok && data.success && data.suggestion) {
        this.inputTarget.value = data.suggestion
        this.inputTarget.focus()
      } else {
        this.showError(data.error || "Could not draft a suggestion.")
      }
    } catch (error) {
      this.showError("Could not draft a suggestion. Please try again.")
    } finally {
      if (button && original !== null) {
        button.disabled = false
        button.textContent = original
      }
    }
  }

  async send() {
    const content = this.inputTarget.value.trim()
    this.clearError()

    if (!content) {
      this.showError("Write a reply before sending.")
      return
    }

    this.sendTarget.disabled = true
    const original = this.sendTarget.textContent
    this.sendTarget.textContent = "Sending…"

    try {
      const response = await fetch(this.sendUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          platform_type: this.selected,
          message_content: content
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.inputTarget.value = ""
        this.resetButton(original)
        await this.refreshThread()
        this.inputTarget.focus()
      } else {
        this.showError(data.error || "Could not send the reply.")
        this.resetButton(original)
      }
    } catch (error) {
      this.showError("Could not send the reply. Please try again.")
      this.resetButton(original)
    }
  }

  // Pull the just-sent message into the thread WITHOUT navigating.
  //
  // Refetches this same conversation and copies across the contents of the thread
  // container alone, matched by the id the server rendered on it. Everything else
  // in the fetched page is discarded, deliberately: the queue top bar in the live
  // DOM keeps the snooze next_id it was rendered with, and a fresh server
  // render cannot be trusted to reproduce it (the lead has just left the Respond
  // queue, so the freshly computed one would be nil).
  //
  // A failure here is NOT a send failure. The reply has already gone out, so the
  // one thing the user must not do is send it again, and the message says so.
  async refreshThread() {
    if (!this.hasThreadTarget || !this.hasThreadUrlValue || !this.threadUrlValue) return

    try {
      const response = await fetch(this.threadUrlValue, {
        headers: { Accept: "text/html" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Thread refresh failed: ${response.status}`)

      const fetched = new DOMParser().parseFromString(await response.text(), "text/html")
      const fresh = fetched.getElementById(this.threadTarget.id)
      if (!fresh) throw new Error("Thread container missing from the refreshed conversation")

      this.threadTarget.innerHTML = fresh.innerHTML
      this.threadTarget.scrollTop = this.threadTarget.scrollHeight
    } catch (error) {
      this.showError(
        "Your reply was sent. The conversation below could not be refreshed, so your message " +
          "is not showing yet. Reload the page to see it, and do not send it again."
      )
    }
  }

  resetButton(label) {
    this.sendTarget.disabled = false
    this.sendTarget.textContent = label
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.style.display = "block"
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.style.display = "none"
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
